@if (form) {
  <form class="form" [formGroup]="form">
    <div class="form-section">
      <div class="form-section-info">
        <h4>Configure Source</h4>
          <div class="description">Choose how Test Objects should be resolved.</div>
      </div>
      <div class="form-section-content">
        <div class="form-item">
          <h4 class="form-label">Source Type</h4>
          <div class="form-item-content">
            <div class="form-item-input source-type-input">
              <p-selectButton formControlName="sourceType" [options]="sourceOptions" styleClass="testobjects-source-type-toggle">
                <ng-template let-option pTemplate="item">
                  <span class="source-type-option-label"
                        [attr.data-testing-id]="'testobjects-source-option-' + option.value | dataTestingId"
                        [pTooltip]="option.label"
                        tooltipPosition="top">
                    {{ option.label }}
                  </span>
                </ng-template>
              </p-selectButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (form.value.sourceType === 'urllist') {
      <div class="form-section">
        <div class="form-section-info">
          <h4>URLs</h4>
          <div class="description">Add one or more URLs to use directly as targets.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <div class="form-item-content">
              <div formArrayName="urls" class="source-list">
                @for (urlCtrl of urls.controls; track trackByIndex(idx, urlCtrl); let idx = $index) {
                  <div [formGroupName]="idx" class="source-entry">
                    <div class="source-entry-input">
                      <input type="text" pInputText placeholder="URL {{idx + 1}}" formControlName="url" [attr.data-testing-id]="'testobjects-source-url-input-' + idx" id="testobjects-source-url-input-{{idx}}" (input)="prependHttpsToUrl(idx)" />
                      @if (urlCtrl.get('url')?.value?.trim()?.length > 0 && urlCtrl.get('url')?.invalid && (urlCtrl.get('url')?.touched || urlCtrl.get('url')?.dirty)) {
                        <div class="validation-icon">
                          <fa-icon aria-hidden="true" [pTooltip]="'URL is not valid'" [tooltipZIndex]="'10000'" icon="exclamation-triangle" class="error-icon"></fa-icon>
                        </div>
                      }
                    </div>
                    @if (urls.length > 1) {
                      <button class="remove-btn" pButton type="button" (click)="removeUrlListItem(idx)" icon="true">
                        <fa-icon icon="trash"></fa-icon>
                      </button>
                    }
                    @if (urls.length === 1) {
                      <button class="remove-btn" pButton type="button" (click)="clearUrl(idx)" icon="true">
                        <fa-icon icon="trash"></fa-icon>
                      </button>
                    }
                  </div>
                }
                <div class="source-actions">
                  <button pButton type="button" (click)="addUrlListItem()" [disabled]="!existingURLValid(form.value['urls'].length - 1)" color="accent" label="Add URL" class="ui-button-secondary" data-testing-id="testobjects-source-add-url-btn">
                    <fa-icon icon="plus"></fa-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    @if (form.value.sourceType === 'testobjectlist') {
      <div class="form-section">
        <div class="form-section-info">
          <h4>Known Test Objects</h4>
          <div class="description">Choose one or more existing Test Objects from this project.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <h4 class="form-label">Test Objects</h4>
            <div class="form-item-content">
              <div class="selector-field">
                <p-multi-select formControlName="selectedTestObjectIds"
                                [options]="testObjectOptions"
                                optionLabel="label"
                                optionValue="value"
                                panelStyleClass="testobjects-source-selector-panel"
                                placeholder="Select existing Test Objects"
                                [filter]="true"
                                [showClear]="false"
                                [showToggleAll]="true"
                                [selectedItemsLabel]="formatKnownTestObjectsSelectionLabel()"
                                data-testing-id="testobjects-source-testobject-multiselect">
                  <ng-template let-option pTemplate="item">
                    <div class="selector-option">
                      <span class="selector-option-label" [pTooltip]="option.label" tooltipPosition="top">{{ option.label }}</span>
                      @if (option.secondary) {
                        <span class="selector-option-secondary" [pTooltip]="option.secondary" tooltipPosition="top">{{ option.secondary }}</span>
                      }
                    </div>
                  </ng-template>
                </p-multi-select>
              </div>
              @if (testObjectOptions.length === 0) {
                <div class="advanced-setup-hint">No Test Objects are available in this project yet.</div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    @if (form.value.sourceType === 'testobjectcollection') {
      <div class="form-section">
        <div class="form-section-info">
          <h4>Test Object Collection</h4>
          <div class="description">Choose one active Test Object Collection from this project.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <h4 class="form-label">Collection</h4>
            <div class="form-item-content">
              <div class="selector-field">
                <p-select formControlName="selectedTestObjectCollectionId"
                          [options]="testObjectCollectionOptions"
                          optionLabel="label"
                          optionValue="value"
                          panelStyleClass="testobjects-source-selector-panel"
                          placeholder="Select a Test Object Collection"
                          [filter]="true"
                          data-testing-id="testobjects-source-testobjectcollection-select">
                  <ng-template let-option pTemplate="item">
                    <div class="selector-option">
                      <span class="selector-option-label" [pTooltip]="option.label" tooltipPosition="top">{{ option.label }}</span>
                      <span class="selector-option-secondary" [pTooltip]="option.secondary" tooltipPosition="top">{{ option.secondary }}</span>
                      @if (option.previewSummary) {
                        <span class="selector-option-preview" [pTooltip]="option.previewSummary" tooltipPosition="top">{{ option.previewSummary }}</span>
                      }
                    </div>
                  </ng-template>
                  <ng-template let-option pTemplate="selectedItem">
                    @if (option) {
                      <div class="selector-option selector-option-selected">
                        <span class="selector-option-label" [pTooltip]="option.label" tooltipPosition="top">{{ option.label }}</span>
                        <span class="selector-option-secondary" [pTooltip]="option.secondary" tooltipPosition="top">{{ option.secondary }}</span>
                      </div>
                    }
                  </ng-template>
                </p-select>
              </div>
              @if (selectedTestObjectCollectionOption; as selectedCollection) {
                <div class="collection-preview-card">
                  <div class="collection-preview-header">
                    <div class="collection-preview-title">Members of {{ selectedCollection.label }}</div>
                    <div class="collection-preview-secondary">{{ selectedCollection.secondary }}</div>
                  </div>
                  @if (visibleSelectedTestObjectCollectionMemberLabels.length > 0) {
                    <div class="collection-preview-members">
                      @for (memberLabel of visibleSelectedTestObjectCollectionMemberLabels; track $index) {
                        <span class="collection-member-chip">{{ memberLabel }}</span>
                      }
                    </div>
                    @if (selectedTestObjectCollectionMemberOverflowCount > 0 || showAllSelectedCollectionMembers) {
                      <button type="button"
                              class="collection-preview-toggle"
                              (click)="toggleSelectedTestObjectCollectionMembers()">
                        {{ showAllSelectedCollectionMembers ? 'Show fewer members' : 'Show all members' }}
                      </button>
                    }
                  } @else if (selectedTestObjectCollectionMembersLoading()) {
                    <div class="advanced-setup-hint">Loading collection members...</div>
                  } @else {
                    <div class="advanced-setup-hint">Member details are currently unavailable for this collection.</div>
                  }
                </div>
              }
              @if (testObjectCollectionOptions.length === 0) {
                <div class="advanced-setup-hint">No active Test Object Collections are available in this project.</div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    @if (form.value.sourceType === 'mobileapp') {
      <div class="form-section mobile-app-source-section">
        <div class="form-section-info">
          <h4>Mobile Application</h4>
          <div class="description">Choose an APK or IPA from this project, or upload a new file.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <h4 class="form-label">Name</h4>
            <div class="form-item-content">
              <input type="text" class="full-wdith" pInputText formControlName="mobileName" placeholder="Checkout mobile app" data-testing-id="testobjects-source-mobile-name-input" />
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">Description</h4>
            <div class="form-item-content">
              <textarea class="full-wdith" pTextarea formControlName="mobileDescription" rows="3" placeholder="Optional short summary for this mobile app." data-testing-id="testobjects-source-mobile-description-input"></textarea>
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">App</h4>
            <div class="form-item-content">
              <div class="mobile-app-picker">
                <div class="selector-field">
                  <p-select formControlName="mobilePackageId"
                            [options]="mobilePackageGroups"
                            [group]="true"
                            optionLabel="name"
                            optionValue="origPackageId"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            placeholder="Select an uploaded APK or IPA"
                            [filter]="true"
                            [showClear]="true"
                            (onChange)="selectExistingMobilePackage()"
                            data-testing-id="testobjects-source-mobile-package-select">
                    <ng-template let-group pTemplate="group">
                      <span class="mobile-package-group-label">{{ group.label }}</span>
                    </ng-template>
                    <ng-template let-pkg pTemplate="item">
                      <div class="selector-option">
                        <span class="selector-option-label">{{ pkg.name }}</span>
                        <span class="selector-option-secondary">{{ pkg.origPackageId }}</span>
                      </div>
                    </ng-template>
                    <ng-template let-pkg pTemplate="selectedItem">
                      @if (pkg) {
                        <span>{{ pkg.name }} ({{ mobilePackagePlatform(pkg) }})</span>
                      }
                    </ng-template>
                  </p-select>
                </div>
                <button pButton type="button" class="ui-button-secondary" label="Add App" (click)="openAddMobileAppTool()" data-testing-id="testobjects-source-mobile-add-app-button">
                  <fa-icon icon="plus"></fa-icon>
                </button>
              </div>
              @if (mobilePackages.length === 0) {
                <div class="advanced-setup-hint">No existing APK or IPA is available. Use “Add App” to upload one.</div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    @if (form.value.sourceType === 'manual') {
      <div class="form-section manual-source-section">
        <div class="form-section-info">
          <h4>Manual Test Object</h4>
          <div class="description">Create a canonical Test Object that humans can reach manually when automated generation is not available.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <h4 class="form-label">Name</h4>
            <div class="form-item-content">
              <input type="text" class="full-wdith" pInputText formControlName="manualName" placeholder="Checkout confirmation page" data-testing-id="testobjects-source-manual-name-input" />
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">Description</h4>
            <div class="form-item-content">
              <textarea class="full-wdith" pTextarea formControlName="manualDescription" rows="3" placeholder="Optional short summary for this Test Object." data-testing-id="testobjects-source-manual-description-input"></textarea>
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">Instructions</h4>
            <div class="form-item-content">
              <textarea class="full-wdith" pTextarea formControlName="manualInstructions" rows="6" placeholder="Describe how a human reaches this Test Object." data-testing-id="testobjects-source-manual-instructions-input"></textarea>
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">Matching</h4>
            <div class="form-item-content">
              <div class="form-item-input">
                <p-selectButton class="full-wdith" formControlName="manualMatchingMode" [options]="manualMatchingModeOptions"></p-selectButton>
              </div>
              @if (form.value.manualMatchingMode === 'exactUrl') {
                <div class="selector-field">
                  <input type="text" class="full-wdith" pInputText formControlName="manualMatchingUrl" placeholder="https://example.com/checkout/confirmation" data-testing-id="testobjects-source-manual-matching-url-input" />
                </div>
              }
              @if (form.value.manualMatchingMode === 'urlRegex') {
                <div class="selector-field">
                  <input type="text" class="full-wdith" pInputText formControlName="manualMatchingRegex" placeholder="^https://example\\.com/checkout(/confirmation)?$" data-testing-id="testobjects-source-manual-matching-regex-input" />
                </div>
              }
              @if (form.value.manualMatchingMode === 'manualOnly') {
                <div class="advanced-setup-hint">
                  Manual-only matching keeps the Test Object for audit planning and manual findings, but excludes it from automated SpecCheck execution.
                </div>
              }
            </div>
          </div>

          <div class="form-item">
            <h4 class="form-label">Labels</h4>
            <div class="form-item-content">
              <wm-tags [tags]="manualLabels" [onlyLocal]="true" (tagsChanged)="manualLabels = $event"></wm-tags>
            </div>
          </div>
        </div>
      </div>
    }

    @if (form.value.sourceType === 'crawled') {
      <div class="form-section">
        <div class="form-section-info">
          <h4>Seed URLs</h4>
          <div class="description">Provide one or more entry points that the crawler should start from.</div>
        </div>
        <div class="form-section-content">
          <div class="form-item">
            <div class="form-item-content">
              <div formArrayName="seedUrls" class="source-list">
                @for (seedUrlCtrl of seedUrls.controls; track trackByIndex(idx, seedUrlCtrl); let idx = $index) {
                  <div [formGroupName]="idx" class="source-entry">
                    <div class="source-entry-input">
                      <input type="text" pInputText placeholder="Seed URL {{idx + 1}}" formControlName="url" [attr.data-testing-id]="'testobjects-source-seed-url-input-' + idx" id="testobjects-source-seed-url-input-{{idx}}" (input)="prependHttpsToSeedUrl(idx)" />
                      @if (seedUrlCtrl.get('url')?.value?.trim()?.length > 0 && seedUrlCtrl.get('url')?.invalid && (seedUrlCtrl.get('url')?.touched || seedUrlCtrl.get('url')?.dirty)) {
                        <div class="validation-icon">
                          <fa-icon aria-hidden="true" [pTooltip]="'Seed URL is not valid'" [tooltipZIndex]="'10000'" icon="exclamation-triangle" class="error-icon"></fa-icon>
                        </div>
                      }
                    </div>
                    @if (seedUrls.length > 1) {
                      <button class="remove-btn" pButton type="button" (click)="removeSeedUrlListItem(idx)" icon="true">
                        <fa-icon icon="trash"></fa-icon>
                      </button>
                    }
                    @if (seedUrls.length === 1) {
                      <button class="remove-btn" pButton type="button" (click)="clearSeedUrl(idx)" icon="true">
                        <fa-icon icon="trash"></fa-icon>
                      </button>
                    }
                  </div>
                }
                <div class="source-actions">
                  <button pButton type="button" (click)="addSeedUrlListItem()" [disabled]="!existingSeedURLValid(form.value['seedUrls'].length - 1)" color="accent" label="Add Seed URL" class="ui-button-secondary" data-testing-id="testobjects-source-add-seed-url-btn">
                    <fa-icon icon="plus"></fa-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <div class="form-section-info advanced-options">
          <div>
            <h4>Advanced Setup</h4>
            <div class="description">Adjust crawl limits, prioritization, and exploration behavior only when you need custom settings.</div>
          </div>
          <p-toggle-switch styleClass="small" formControlName="crawlAdvancedSetup" data-testing-id="testobjects-source-crawl-advanced-setup-toggle"></p-toggle-switch>
        </div>
        <div class="form-section-content">
          @if (!form.get('crawlAdvancedSetup')?.value) {
            <div class="advanced-setup-hint">
              Default crawl settings will be used.
            </div>
          }

          @if (form.get('crawlAdvancedSetup')?.value) {
            <div class="form-item">
              <h4 class="form-label">Limits</h4>
              <div class="form-item-content">
                <div class="crawl-grid">
                  <div class="crawl-field">
                    <label for="crawl-max-states">Max States</label>
                    <input id="crawl-max-states" type="number" pInputText formControlName="crawlMaxStates" data-testing-id="testobjects-source-crawl-max-states-input">
                  </div>
                  <div class="crawl-field">
                    <label for="crawl-max-depth">Max Depth</label>
                    <input id="crawl-max-depth" type="number" pInputText formControlName="crawlMaxDepth" data-testing-id="testobjects-source-crawl-max-depth-input">
                  </div>
                  <div class="crawl-field">
                    <label for="crawl-max-width">Max Width</label>
                    <input id="crawl-max-width" type="number" pInputText formControlName="crawlMaxWidth" data-testing-id="testobjects-source-crawl-max-width-input">
                  </div>
                  <div class="crawl-field">
                    <label for="crawl-max-duration">Max Crawl Duration</label>
                    <input id="crawl-max-duration" type="text" pInputText formControlName="crawlMaxCrawlDuration" data-testing-id="testobjects-source-crawl-max-duration-input">
                  </div>
                </div>
              </div>
            </div>

            <div class="form-item">
              <h4 class="form-label">Prioritizing Strategy</h4>
              <div class="form-item-content">
                <div class="form-item-input">
                  <p-selectButton formControlName="crawlPrioritizingStrategy" [options]="crawlPrioritizingOptions"></p-selectButton>
                </div>
              </div>
            </div>

            <div class="form-item">
              <h4 class="form-label">Exploration Options</h4>
              <div class="form-item-content">
                <div class="crawl-options">
                  <div class="crawl-option">
                    <p-checkbox inputId="crawl-use-sitemap" formControlName="crawlUseSitemap" [binary]="true"></p-checkbox>
                    <label for="crawl-use-sitemap">Use sitemap</label>
                  </div>
                  <div class="crawl-option">
                    <p-checkbox inputId="crawl-restrict-seed-hosts" formControlName="crawlRestrictToSeedHosts" [binary]="true"></p-checkbox>
                    <label for="crawl-restrict-seed-hosts">Restrict to seed hosts</label>
                  </div>
                  <div class="crawl-option">
                    <p-checkbox inputId="crawl-use-anchor-tags" formControlName="crawlUseAnchorTags" [binary]="true"></p-checkbox>
                    <label for="crawl-use-anchor-tags">Use anchor tags</label>
                  </div>
                  <div class="crawl-option">
                    <p-checkbox inputId="crawl-use-form-actions" formControlName="crawlUseFormActions" [binary]="true"></p-checkbox>
                    <label for="crawl-use-form-actions">Use form actions</label>
                  </div>
                  <div class="crawl-option">
                    <p-checkbox inputId="crawl-use-clickable-elements" formControlName="crawlUseClickableElements" [binary]="true"></p-checkbox>
                    <label for="crawl-use-clickable-elements">Use clickable elements</label>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <div class="actions">
      <button pButton type="button" class="ui-button-secondary" label="Cancel" (click)="cancel()" [disabled]="confirming" data-testing-id="testobjects-source-cancel-btn"></button>
      <button pButton type="button" [label]="confirming ? 'Resolving...' : 'OK'" (click)="confirm()" [disabled]="!form.valid || confirming" [loading]="confirming" data-testing-id="testobjects-source-ok-btn"></button>
    </div>
  </form>
}
